import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Search, 
  MessageSquare,
  MoreHorizontal,
  Clock,
  AlertCircle,
  CheckCircle2,
  Eye,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  Link2,
  Upload,
  RefreshCw,
  XCircle,
  FolderKanban,
  UserPlus,
  User
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';

const TicketsPage = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    project_id: '',
    client_id: '',
    subject: '',
    message: '',
    priority: 'normal',
    attachment_url: '',
    link_url: ''
  });
  const [clients, setClients] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [selectedTicketForReopen, setSelectedTicketForReopen] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTicketForAssign, setSelectedTicketForAssign] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchTickets();
    fetchProjects();
    if (isAgency) {
      fetchClients();
      fetchTeamMembers();
    }
  }, [isAgency]);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/tickets/');
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'team_member' } });
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'client' } });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects/');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('category', 'documents');
      formData.append('description', 'Pièce jointe ticket');
      
      const response = await api.post('/supports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data.support?.url || null;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload du fichier');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleMarkResolved = async (ticketId) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status: 'resolved' });
      toast.success('Ticket marqué comme résolu');
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleReopenTicket = async () => {
    if (!selectedTicketForReopen || !reopenReason.trim()) {
      toast.error('Veuillez indiquer la raison de la réouverture');
      return;
    }
    
    try {
      await api.put(`/tickets/${selectedTicketForReopen.id}`, { 
        status: 'open',
        reopen_reason: reopenReason
      });
      toast.success('Ticket réouvert');
      setShowReopenModal(false);
      setSelectedTicketForReopen(null);
      setReopenReason('');
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de la réouverture');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Veuillez remplir le sujet et le message');
      return;
    }
    
    // For clients, project is optional
    if (isAgency && !newTicket.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    setCreating(true);
    try {
      // Upload attachment if present
      let attachmentUrl = null;
      if (attachmentFile) {
        attachmentUrl = await handleFileUpload(attachmentFile);
      }
      
      const ticketData = {
        ...newTicket,
        attachment_url: attachmentUrl || newTicket.attachment_url,
        project_id: newTicket.project_id || null
      };
      
      await api.post('/tickets/', ticketData);
      toast.success('Ticket créé avec succès !');
      setShowCreateModal(false);
      setNewTicket({ project_id: '', client_id: '', subject: '', message: '', priority: 'normal', attachment_url: '', link_url: '' });
      setAttachmentFile(null);
      fetchTickets();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/tickets/${ticketId}/status`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchTickets();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedTicketForAssign) return;
    try {
      await api.put(`/tickets/${selectedTicketForAssign.id}/assign`, { 
        assigned_to: selectedAssignee || null 
      });
      toast.success(selectedAssignee ? 'Ticket assigné !' : 'Assignation retirée');
      setShowAssignModal(false);
      setSelectedTicketForAssign(null);
      setSelectedAssignee('');
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || t.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  }).sort((a, b) => {
    // Sorting logic
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Handle dates
    if (sortField === 'created_at' || sortField === 'updated_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    
    // Handle priority ordering
    if (sortField === 'priority') {
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      aVal = priorityOrder[aVal] || 0;
      bVal = priorityOrder[bVal] || 0;
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    return sortOrder === 'asc' 
      ? <ChevronUp className="w-4 h-4 ml-1" />
      : <ChevronDown className="w-4 h-4 ml-1" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      open: 'Ouvert',
      in_progress: 'En cours',
      resolved: 'Résolu',
      closed: 'Fermé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.open)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600',
    };
    const labels = {
      low: 'Basse',
      normal: 'Normale',
      high: 'Haute',
      urgent: 'Urgente',
    };
    return (
      <Badge className={cn('font-medium', styles[priority] || styles.normal)}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Tickets
            </h1>
            <p className="text-[#64748b] mt-1">
              {openTickets} ticket(s) ouvert(s) sur {tickets.length}
            </p>
          </div>
          
          <Button 
            onClick={() => setShowCreateModal(true)}
            style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
            className="font-bold rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau ticket
          </Button>
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher un ticket..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-default w-full md:w-[150px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="open">Ouverts</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="resolved">Résolus</SelectItem>
                  <SelectItem value="closed">Fermés</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="input-default w-full md:w-[150px]">
                  <SelectValue placeholder="Priorité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="low">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                Aucun ticket trouvé
              </h3>
              <p className="text-[#64748b]">
                Tous les tickets sont traités !
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                    <TableHead 
                      className="font-semibold cursor-pointer select-none whitespace-nowrap"
                      style={{ color: theme.colors?.primary }}
                      onClick={() => handleSort('subject')}
                    >
                      <div className="flex items-center">
                        Sujet
                        {getSortIcon("subject")}
                      </div>
                    </TableHead>
                    {isAgency && (
                      <TableHead className="font-semibold whitespace-nowrap" style={{ color: theme.colors?.primary }}>
                        Client
                      </TableHead>
                    )}
                    <TableHead className="font-semibold whitespace-nowrap" style={{ color: theme.colors?.primary }}>
                      Projet
                    </TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer select-none whitespace-nowrap"
                      style={{ color: theme.colors?.primary }}
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Statut
                        {getSortIcon("status")}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer select-none whitespace-nowrap"
                      style={{ color: theme.colors?.primary }}
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center">
                        Priorité
                        {getSortIcon("priority")}
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold whitespace-nowrap text-center" style={{ color: theme.colors?.primary }}>
                      Messages
                    </TableHead>
                    {isAgency && (
                      <TableHead className="font-semibold whitespace-nowrap" style={{ color: theme.colors?.primary }}>
                        Assigné
                      </TableHead>
                    )}
                    <TableHead 
                      className="font-semibold cursor-pointer select-none whitespace-nowrap"
                      style={{ color: theme.colors?.primary }}
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">
                        Créé le
                        {getSortIcon("created_at")}
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow 
                      key={ticket.id}
                      className="cursor-pointer hover:bg-[#f8fafc] transition-colors"
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar 
                            name={ticket.user_name || 'User'}
                            size="sm"
                            type="client"
                          />
                          <div className="min-w-0">
                            <p className="font-medium truncate max-w-[250px]" style={{ color: theme.colors?.primary }}>
                              {ticket.subject}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      {isAgency && (
                        <TableCell>
                          <span className="text-sm text-[#64748b]">{ticket.user_name || '-'}</span>
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="text-sm text-[#64748b]">{ticket.project_name || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(ticket.status)}
                      </TableCell>
                      <TableCell>
                        {getPriorityBadge(ticket.priority)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">
                          {ticket.messages?.length || 0}
                        </Badge>
                      </TableCell>
                      {isAgency && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {ticket.assigned_to_name ? (
                              <span className="text-sm text-slate-700">{ticket.assigned_to_name}</span>
                            ) : (
                              <span className="text-sm text-slate-400 italic">Non assigné</span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTicketForAssign(ticket);
                                setSelectedAssignee(ticket.assigned_to || '');
                                setShowAssignModal(true);
                              }}
                            >
                              <UserPlus className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="text-sm text-[#64748b] whitespace-nowrap">
                          {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tickets/${ticket.id}`);
                            }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir le ticket
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tickets/${ticket.id}`);
                            }}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Répondre
                            </DropdownMenuItem>
                            {isAgency && ticket.status !== 'resolved' && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleMarkResolved(ticket.id);
                              }}>
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Marquer résolu
                              </DropdownMenuItem>
                            )}
                            {ticket.status === 'resolved' && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTicketForReopen(ticket);
                                setShowReopenModal(true);
                              }}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Demander réouverture
                              </DropdownMenuItem>
                            )}
                            {ticket.project_id && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${ticket.project_id}`);
                              }}>
                                <FolderKanban className="w-4 h-4 mr-2" />
                                Voir le projet lié
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        {/* Create Ticket Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              {/* Client selector for agency */}
              {isAgency && (
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select 
                    value={newTicket.client_id} 
                    onValueChange={(value) => setNewTicket({ ...newTicket, client_id: value })}
                  >
                    <SelectTrigger className="input-default">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name}
                          {client.company_name && ` (${client.company_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Projet (optionnel)</Label>
                <Select 
                  value={newTicket.project_id || "none"} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, project_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun projet</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sujet *</Label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Résumé du problème"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue placeholder="Priorité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="normal">Normale</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Décrivez votre demande en détail..."
                  className="input-default min-h-[100px]"
                  required
                />
              </div>
              
              {/* Attachment */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Pièce jointe (optionnel)
                </Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  accept="image/*,.pdf,.doc,.docx"
                  className="hidden"
                />
                {attachmentFile ? (
                  <div className="flex items-center gap-2 p-3 bg-[#f8fafc] rounded-lg">
                    <ImageIcon className="w-5 h-5 text-[#64748b]" />
                    <span className="text-sm flex-1 truncate">{attachmentFile.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAttachmentFile(null)}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter une image ou un fichier
                  </Button>
                )}
              </div>
              
              {/* Link */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Lien (optionnel)
                </Label>
                <Input
                  value={newTicket.link_url}
                  onChange={(e) => setNewTicket({ ...newTicket, link_url: e.target.value })}
                  placeholder="https://example.com/page"
                  className="input-default"
                  type="url"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={creating || uploading}
                  className="rounded-full" 
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {creating || uploading ? 'Création...' : 'Créer le ticket'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reopen Ticket Modal */}
        <Dialog open={showReopenModal} onOpenChange={setShowReopenModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Réouvrir le ticket</DialogTitle>
              <DialogDescription>
                Indiquez la raison pour laquelle vous souhaitez réouvrir ce ticket.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Raison de la réouverture..."
                className="min-h-[100px]"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReopenModal(false)}>Annuler</Button>
              <Button onClick={handleReopenTicket} style={{ backgroundColor: theme.colors?.accent }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Réouvrir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Ticket Modal */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Assigner le ticket</DialogTitle>
              <DialogDescription>
                Sélectionnez un membre de l'équipe pour traiter ce ticket.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label className="mb-2 block">Assigner à</Label>
              <Select value={selectedAssignee || '_unassigned'} onValueChange={(v) => setSelectedAssignee(v === '_unassigned' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un membre..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unassigned">Non assigné</SelectItem>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {member.first_name} {member.last_name}
                        {member.service && <span className="text-slate-400">({member.service})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>Annuler</Button>
              <Button onClick={handleAssignTicket} style={{ backgroundColor: theme.colors?.accent }}>
                <UserPlus className="w-4 h-4 mr-2" />
                Assigner
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default TicketsPage;
